
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { Parameter, ParsedParameter, parseParameterValue, Parameters, fetchParameters, createParametersQueryKey, extractBusinessParameters, extractBadgeConfiguration, extractTimeFormat, extractCategoryBrowsing, extractAllowedLocation, extractFeaturedGrouping, extractDiscountSettings } from '@communitly/shared';

/**
 * Hook to fetch application parameters
 */
export function useParameters() {
  const { currentCommunity } = useCommunity();
  const communityId = currentCommunity?.id;

  return useQuery({
    queryKey: createParametersQueryKey(communityId),
    queryFn: () => fetchParameters(supabase, communityId),
    enabled: !!communityId,
  });
}

/**
 * Hook to fetch business-related parameters
 */
export function useBusinessParameters() {
  const { data, isLoading, error } = useParameters();
  
  const businessParams = data?.parameters ? extractBusinessParameters(data.parameters) : {
    forceDiscount: false,
    partialDiscountAllowed: true,
    defaultBannerUrl: ''
  };
  
  return {
    ...businessParams,
    isLoading,
    error
  };
}

/**
 * Hook to fetch time format parameters
 */
export function useTimeFormatParameters() {
  const { data, isLoading, error } = useParameters();
  
  const timeDisplayFormat = data?.parameters ? extractTimeFormat(data.parameters) : '12';
  
  return {
    timeDisplayFormat,
    isLoading,
    error
  };
}

/**
 * Hook to fetch category browsing parameter
 */
export function useCategoryBrowsingParameter() {
  const { data, isLoading, error } = useParameters();
  
  const categoryBrowsingEnabled = data?.parameters ? extractCategoryBrowsing(data.parameters) : false;
  
  return {
    categoryBrowsingEnabled,
    isLoading,
    error
  };
}

/**
 * Hook to fetch badge configuration for home page
 */
export function useBadgeConfiguration() {
  const { data, isLoading, error } = useParameters();
  
  const badgeSections = data?.parameters ? extractBadgeConfiguration(data.parameters) : [];
  
  return {
    badgeSections,
    isLoading,
    error
  };
}

/**
 * Hook to fetch location-related parameters
 */
export function useLocationParameters() {
  const { data, isLoading, error } = useParameters();
  
  const allowedLocation = data?.parameters ? extractAllowedLocation(data.parameters) : 'Leicester';
  
  return {
    allowedLocation,
    isLoading,
    error
  };
}

/**
 * Hook to fetch featured businesses grouping parameter
 */
export function useFeaturedGroupingParameter() {
  const { data, isLoading, error } = useParameters();
  
  const groupByCategory = data?.parameters ? extractFeaturedGrouping(data.parameters) : false;
  
  return {
    groupByCategory,
    isLoading,
    error
  };
}

/**
 * Hook to fetch discount display settings
 */
export function useDiscountSettings() {
  const { data, isLoading, error } = useParameters();
  
  const discountSettings = data?.parameters ? extractDiscountSettings(data.parameters) : {
    shouldDisplayDiscountCode: false,
    forceDiscount: false,
    partialDiscountAllowed: true
  };
  
  return {
    ...discountSettings,
    isLoading,
    error
  };
}
